// React
import React, { useState, useEffect, useCallback } from 'react';

// Prop types
import PropTypes from 'prop-types';

// Redux
import { connect } from 'react-redux';
import { withRoomContext } from '../../../RoomContext';

// Component
import { useIntl } from 'react-intl';
import { permissions } from '../../../permissions';
import { makePermissionSelector } from '../../Selectors';

// Mui styles
import { withStyles } from '@material-ui/core/styles';

// Mui core
import Box from '@material-ui/core/Box';
import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';

// Icon
import IconButton from '@material-ui/core/IconButton';
import SendIcon from '@material-ui/icons/Send';

const styles = (theme) =>
	({
		root : {
			padding      : theme.spacing(1),
			display      : 'flex',
			alignItems   : 'center',
			borderRadius : 0,
			position     : 'relative'
		},
		input : {
			width    : `calc(100% - ${theme.spacing(7)}px)`,
			position : 'absolute',
			bottom   : 8
		},
		textArea : {
			flex                        : 1,
			borderRadius                : '0.5rem',
			padding                     : '3% 5%',
			fontSize                    : '0.84rem',
			width                       : '100%',
			background                  : 'white',
			'& $textAreaNotchedOutline' : {
				borderColor : 'var(--text-color)'
			},
			'&:hover $textAreaNotchedOutline' : {
				borderColor : 'var(--text-color)'
			},
			'&.Mui-focused $textAreaNotchedOutline' : {
				borderColor : 'var(--text-color)',
				borderWidth : '1px'
			},
			'& > textarea' :
			{
				minHeight : '24px !important'
			},
			[theme.breakpoints.down('lg')] : {
				padding : '4% 5%'
			}
		},
		limitTextAreaSize :
		{
			'& > textarea' :
			{
				height : '24px !important'
			}
		},
		textAreaNotchedOutline : {
			borderColor : 'var(--text-color)'
		},
		action : {
			width          : '100%',
			display        : 'flex',
			justifyContent : 'flex-end'
		},
		iconButton : {
			padding         : 6,
			backgroundColor : 'var(--text-color)',
			color           : 'white',
			'&:hover'       : {
				backgroundColor : 'var(--text-color)',
				color           : 'white'
			}
		}
	});

const ChatInput = (props) =>
{
	const [ message, setMessage ] = useState('');

	const intl = useIntl();

	const createNewMessage = (text, sender, name, picture) =>
		({
			type : 'message',
			text,
			time : Date.now(),
			name,
			sender,
			picture
		});

	const {
		roomClient,
		displayName,
		picture,
		canChat,
		classes,
		setChatFocused
	} = props;

	const handleChange = (e) =>
	{
		setMessage(e.target.value);
	};

	const handleSendMessage =useCallback(() =>
	{
		if (message)
		{
			const sendMessage = createNewMessage(message, 'response', displayName, picture);

			roomClient.sendChatMessage(sendMessage);

			setMessage('');
		}
	}, [ message, displayName, picture, roomClient, setMessage ]);

	const handleChatFocused = (value) =>
	{
		if (setChatFocused)
		{
			setChatFocused(value);
		}
	};

	let textFieldInputCss = classes.textArea;

	if (!message)
	{
		textFieldInputCss = `${classes.textArea} ${classes.limitTextAreaSize}`;
	}

	const sendMessageByKeydown = useCallback((e) =>
	{
		if (e.shiftKey)
		{
			if (e.keyCode === 13)
			{
				e.preventDefault();

				handleSendMessage();
			}
		}

	}, [ handleSendMessage ]);

	useEffect(() =>
	{
		document.addEventListener('keydown', sendMessageByKeydown);

		return () =>
		{
			// Clean up the subscription
			document.removeEventListener('keydown', sendMessageByKeydown);
		};
	}, [ sendMessageByKeydown ]);

	return (
		<Paper className={classes.root}>
			<TextField
				classes={{
					root : classes.input
				}}
				InputProps={{
					classes : {
						root           : textFieldInputCss,
						notchedOutline : classes.textAreaNotchedOutline
					}
				}}
				variant='outlined'
				placeholder={intl.formatMessage({
					id             : 'label.chatInput',
					defaultMessage : 'Enter chat message...'
				})}
				value={message || ''}
				multiline
				minRows={1}
				disabled={!canChat}
				onChange={handleChange}
				onFocus={() => handleChatFocused(true)}
				onBlur={() => handleChatFocused(false)}
			/>
			<Box className={classes.action}>
				<IconButton
					color='primary'
					className={classes.iconButton}
					aria-label='Send'
					disabled={!canChat}
					onClick={handleSendMessage}
				>
					<SendIcon />
				</IconButton>
			</Box>
		</Paper>
	);
};

ChatInput.propTypes =
{
	roomClient     : PropTypes.object.isRequired,
	displayName    : PropTypes.string,
	picture        : PropTypes.string,
	canChat        : PropTypes.bool.isRequired,
	classes        : PropTypes.object.isRequired,
	setChatFocused : PropTypes.func
};

const makeMapStateToProps = () =>
{
	const hasPermission = makePermissionSelector(permissions.SEND_CHAT);

	const mapStateToProps = (state) =>
		({
			displayName : state.settings.displayName,
			picture     : state.me.picture,
			canChat     : hasPermission(state)
		});

	return mapStateToProps;
};

export default withRoomContext(
	connect(
		makeMapStateToProps,
		null,
		null,
		{
			areStatesEqual : (next, prev) =>
			{
				return (
					prev.room === next.room &&
					prev.me.roles === next.me.roles &&
					prev.peers === next.peers &&
					prev.settings.displayName === next.settings.displayName &&
					prev.me.picture === next.me.picture
				);
			}
		}
	)(withStyles(styles)(ChatInput))
);